import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  notificationPreviewApi,
  notificationSettingsQueryKeys,
  type MessagingTemplate,
  type NotificationEventRow,
  type TemplatePreview,
  type TemplatePreviewRequest,
} from "../../shared/api/notificationSettings";
import { apiError } from "../../shared/api/client";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { looksDynamic, sampleOfParam } from "./EventChannelTemplates";

/** Fields Addy Message fills from the clinic profile — no event supplies them (D-108). */
const PLATFORM_FIELD = /^clinic\./;

export type ParamSource = "system" | "platform" | "fixed" | "missing";

export interface ParamRow {
  /** 1-based, by the template's own order — the number the "Bệnh nhân nhận" panel uses too. */
  n: number;
  param: string;
  source: ParamSource;
  /** What a person reads: "Ngày khám", not appointment.date. */
  label: string;
  value: string | null;
  warning: string | null;
}

/** ①②③… — stable per template position, so both panels agree on which slot is which. */
export function circled(n: number): string {
  return n >= 1 && n <= 20 ? String.fromCodePoint(0x245f + n) : `(${n})`;
}

/**
 * The rows of "Tham số của mẫu" (#19586), from the platform's own preview (#19584): each declared
 * parameter of the template, where its value comes from, and what is wrong with it. Ordered by the
 * template; the caller decides what to show first.
 */
export function paramRows(
  template: MessagingTemplate | undefined,
  channel: string,
  preview: TemplatePreview,
  labels: Record<string, string>,
): ParamRow[] {
  const rendition = template?.renditions.find((r) => r.channel === channel);
  const declared = rendition?.params?.length
    ? [...rendition.params].sort((a, b) => a.position - b.position).map((p) => p.key)
    : Object.keys(preview.providerParams);
  return declared.map((param, i) => {
    const raw = preview.providerParams[param];
    const value = raw === null || raw === undefined || String(raw).trim() === "" ? null : String(raw);
    const from = preview.sources[param];
    let source: ParamSource;
    if (!value || preview.missing.includes(param) || !from) source = "missing";
    else if (from === param) source = "fixed";
    else if (PLATFORM_FIELD.test(from)) source = "platform";
    else source = "system";
    const label =
      source === "fixed" || source === "missing"
        ? source === "fixed"
          ? "Giá trị cố định"
          : "Chưa có giá trị"
        : (preview.sourceLabels[from] ?? labels[from] ?? from);
    let warning: string | null = null;
    if (source === "missing") warning = "Không có giá trị — tin sẽ bị từ chối.";
    else if (preview.tooLong?.includes(param)) warning = "Dài quá giới hạn của Zalo.";
    // #19586 — the guard the editor has had since #19565, now also where nobody is editing: a
    // fixed value is the same in every message, so a date there is the same date for everyone.
    else if (source === "fixed" && value && looksDynamic(value, sampleOfParam(template, param)))
      warning = "Giống nhau trong mọi tin, nhưng trông như ngày/giờ hoặc giá trị ví dụ — kiểm tra lại.";
    return { n: i + 1, param, source, label, value, warning };
  });
}

/** What a clinic reads in "Nguồn giá trị": where the value comes from, said from their side. */
export const SOURCE_TEXT: Record<ParamSource, string> = {
  system: "Theo từng bệnh nhân",
  platform: "Từ hồ sơ phòng khám",
  fixed: "Cố định (mọi tin như nhau)",
  missing: "Thiếu",
};

export const SOURCE_HINT: Record<ParamSource, string> = {
  system: "HIS điền từ lịch hẹn / hồ sơ của người nhận — mỗi tin một giá trị khác.",
  platform: "Lấy từ thông tin phòng khám (tên, địa chỉ, hotline…).",
  fixed: "Giá trị phòng khám tự nhập — giống nhau trong mọi tin.",
  missing: "Không có nguồn nào điền — Zalo sẽ từ chối gửi tin.",
};

const ALL_HINTS = (Object.keys(SOURCE_TEXT) as ParamSource[])
  .map((s) => `${SOURCE_TEXT[s]}: ${SOURCE_HINT[s]}`)
  .join("\n");

/**
 * The preview of one cell, saved or draft (#19584), shared by "Tham số của mẫu" and "Bệnh nhân
 * nhận": both read the same query, so the two panels cannot disagree. A draft's values are
 * debounced as a string — an object is a new identity on every render.
 */
export function useTemplatePreview(
  event: NotificationEventRow,
  req: TemplatePreviewRequest,
  enabled: boolean,
) {
  const key = useDebounce(JSON.stringify(req), 350);
  const body = JSON.parse(key) as TemplatePreviewRequest;
  return useQuery({
    queryKey: notificationSettingsQueryKeys.preview(event.eventKey, body),
    queryFn: () => notificationPreviewApi.preview(event.eventKey, body),
    enabled: enabled && !!body.templateCode,
    retry: false,
    staleTime: 30_000,
  });
}

/** Above this many parameters, only the ones that need a look are shown until asked. */
const COLLAPSE_AT = 6;

/**
 * "Tham số của mẫu" (#19586) — replaces both the "Giá trị cố định" line and the "Xem trước nội
 * dung" table, which showed the same data twice under two names, one of them wrong.
 */
export function ParamTable({
  channel,
  template,
  query,
  labels,
  draft,
  canEditFixed,
  onEditFixed,
}: {
  channel: string;
  template: MessagingTemplate | undefined;
  query: ReturnType<typeof useTemplatePreview>;
  labels: Record<string, string>;
  /** An unsaved fixed-value draft is on screen: the rows show it, marked "Chưa lưu". */
  draft: boolean;
  canEditFixed: boolean;
  onEditFixed: () => void;
}) {
  const [all, setAll] = useState(false);
  if (query.isLoading) {
    return <small className="text-xs text-muted-foreground">Đang lấy tham số của mẫu…</small>;
  }
  if (query.isError) {
    const err = apiError(query.error);
    return (
      <small className="flex flex-wrap items-center gap-2 text-xs text-destructive" role="alert">
        <span>{err.detail}</span>
        {err.status === 503 && (
          <button type="button" className="text-action underline" onClick={() => query.refetch()}>
            Thử lại
          </button>
        )}
      </small>
    );
  }
  if (!query.data) return null;
  const rows = paramRows(template, channel, query.data, labels);
  const needsLook = (r: ParamRow) => r.source === "missing" || r.source === "fixed" || !!r.warning;
  // What needs a look first, then the template's own order.
  const ordered = [...rows].sort(
    (a, b) => Number(!!b.warning) - Number(!!a.warning) || a.n - b.n,
  );
  const collapsed = rows.length >= COLLAPSE_AT && !all;
  const shown = collapsed ? ordered.filter(needsLook) : ordered;
  const fixedCount = rows.filter((r) => r.source === "fixed").length;

  return (
    <div className="grid content-start gap-1" data-testid={`param-table-${channel}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium uppercase tracking-wide text-muted-foreground">
          Tham số của mẫu ({rows.length}
          {fixedCount ? ` · ${fixedCount} cố định` : ""})
        </span>
        {draft && (
          <span className="rounded bg-amber-100 px-1.5 font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
            Chưa lưu
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <small className="text-xs text-muted-foreground">Mẫu không khai báo tham số.</small>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b bg-muted/50 text-muted-foreground">
                <th className="w-6 px-2 py-1 font-medium">#</th>
                <th className="px-2 py-1 font-medium">Tham số</th>
                <th className="whitespace-nowrap px-2 py-1 font-medium">
                  <span title={ALL_HINTS} className="cursor-help">
                    Nguồn giá trị ⓘ
                  </span>
                </th>
                <th className="px-2 py-1 font-medium">Ví dụ</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.param} className="border-b align-top last:border-b-0">
                  <td className="w-6 px-2 py-1.5 text-muted-foreground">{circled(r.n)}</td>
                  <td className="min-w-0 px-2 py-1.5">
                    <div className={r.source === "missing" ? "text-destructive" : ""}>{r.label}</div>
                    <div className="break-all font-mono text-[12px] text-muted-foreground">{r.param}</div>
                    {r.warning && (
                      <div className="mt-0.5 text-amber-800 dark:text-amber-300" data-testid="param-warning">
                        ⚠️ {r.warning}
                      </div>
                    )}
                  </td>
                  <td
                    className="whitespace-nowrap px-2 py-1.5 text-muted-foreground"
                    title={SOURCE_HINT[r.source]}
                  >
                    {SOURCE_TEXT[r.source]}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="break-words">{r.value ?? "—"}</span>
                    {r.source === "fixed" && canEditFixed && (
                      <button
                        type="button"
                        className="ml-2 text-action underline-offset-2 hover:underline"
                        onClick={onEditFixed}
                      >
                        Sửa
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length >= COLLAPSE_AT && (
        <button
          type="button"
          className="justify-self-start text-xs text-action underline-offset-2 hover:underline"
          onClick={() => setAll((v) => !v)}
        >
          {all ? "Chỉ hiện tham số cần xem" : `Hiện tất cả (${rows.length})`}
        </button>
      )}
      {/* #19594 — said once, here; where the values are computed is not the clinic's concern. */}
      <small className="text-xs text-muted-foreground">
        Giá trị ví dụ. Tin thật dùng thông tin của từng bệnh nhân.
      </small>
    </div>
  );
}
